"use client";

import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useState } from "react";
import { GradientText } from "@/components/ui/gradient-text";
import { cn } from "@/lib/utils";

const FAQS: { q: string; a: string }[] = [
  {
    q: "How does moongate handle my wallet?",
    a: "moongate provisions and secures a wallet for you — no seed phrases, no browser extensions. Sign in and your assets are ready to trade.",
  },
  {
    q: "Which chains and assets are supported?",
    a: "20+ chains and 12,000+ assets — major L1s and L2s plus tokenized stocks. Cross-chain routing is handled automatically, with no bridges or wrapped tokens.",
  },
  {
    q: "How do I buy and sell with fiat?",
    a: "Top up with a card, Apple Pay, or bank transfer and receive any token or tokenized stock on the chain you choose. Cashing out to fiat works the same way, in reverse.",
  },
  {
    q: "What are the fees on swaps and trades?",
    a: "A flat 100 bps on swaps and trades. Network (gas) fees may also apply depending on the blockchain — those go to the network, not to moongate.",
  },
  {
    q: "What are baskets and prediction markets?",
    a: "Baskets let you buy a whole narrative — a curated bundle of tokenized stocks and crypto — in a single tap. Prediction markets let you take a position on real-world outcomes and exit any time.",
  },
  {
    q: "How do baskets work?",
    a: "A basket is a curated mix of tokens, each with a target weight, that you buy in a single tap. Portfolio management and rebalancing are handled automatically in the background, so you don't have to trade each token yourself. Open a basket to see exactly what's inside, its allocation, and how it's performed. You stay in control the whole time: withdraw any single token, or exit the whole basket back to your main wallet, whenever you want.",
  },
  {
    q: "What is rebalancing, and is it automatic?",
    a: "As prices move, the tokens in a basket drift away from their target weights — winners grow, laggards shrink. Rebalancing trades inside the basket to bring it back to its target mix. You can rebalance on demand with a single tap, or turn on auto-rebalance and we'll keep the basket aligned to its target on a regular schedule. Either way, the tokens stay in your basket until you choose to withdraw them.",
  },
  {
    q: "Can I embed moongate in my own app?",
    a: "Yes. Our Wallet-as-a-Service SDK drops the same wallet and trade widget into your product in minutes. Get in touch and we'll help you get started.",
  },
  {
    q: "Is it available in my region?",
    a: "Availability varies by jurisdiction. Connect a wallet to see what's enabled for you — some markets, like tokenized stocks and predictions, are region-gated.",
  },
  {
    q: "Can I export my private keys?",
    a: "Yes — your keys are exportable at any time. Head to Profile → Settings → Manage wallets to view and export them.",
  },
  {
    q: "How do I delete my account?",
    a: "You can delete your account anytime from Profile → Settings → Delete account. Before you do, please make sure you've completed two things, because deletion is permanent and cannot be reversed:\n\n1. Withdraw any funds to a wallet or exchange you control.\n2. Save any transaction history or records you'll want to keep.\n\nOnce your account is deleted, it's removed from our database and we can no longer access or recover it for you. Any funds left behind will be unrecoverable, so we're unable to take responsibility for losses after deletion.",
  },
];

function FaqItem({ q, a, open, onToggle }: { q: string; a: string; open: boolean; onToggle: () => void }) {
  return (
    <div className="border-b border-border/60">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
        aria-expanded={open}
      >
        <span className="text-base font-medium sm:text-lg">{q}</span>
        <Plus
          className={cn(
            "size-5 shrink-0 text-muted-foreground transition-transform duration-300",
            open && "rotate-45 text-primary",
          )}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="whitespace-pre-line pb-5 pr-10 text-muted-foreground">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="relative px-4 py-16 sm:py-20">
      <div className="mx-auto max-w-3xl">
        <div className="mb-10 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">FAQ</p>
          <h2 className="mt-3 text-balance text-4xl font-semibold tracking-tight sm:text-5xl">
            Questions, <GradientText>answered.</GradientText>
          </h2>
        </div>
        <div>
          {FAQS.map((f, i) => (
            <FaqItem
              key={f.q}
              q={f.q}
              a={f.a}
              open={open === i}
              onToggle={() => setOpen(open === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
